import { TreeItemProps } from '@mui/x-tree-view';
import { useTreeItemModel, useTreeItemUtils } from '@mui/x-tree-view/hooks';
import { TreeViewCancellableEventHandler } from '@mui/x-tree-view/models';
import React, { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../../app/store';
import {
  HandlersLibraryTreeNode,
  useDeleteApiLibraryDirectoriesByDirectoryIdMutation,
} from '../../../../../app/athenaApiGen';
import {
  setPendingCreateItemId,
  setPendingEditItemId,
} from '../../../../../app/library/librarySlice';
import { AthenaTreeItem } from '../../../StyledComponents';
import CustomLabel from './CustomLabel';
import CustomLabelInput from './CustomLabelInput';

interface TCustomTreeItemProps extends TreeItemProps {
  itemId: string;
}

const CustomTreeItem = React.forwardRef(function CustomTreeItem(
  props: TCustomTreeItemProps,
  ref: React.Ref<HTMLLIElement> | undefined,
) {
  const { interactions, status } = useTreeItemUtils({
    itemId: props.itemId,
    children: props.children,
  });
  const item = useTreeItemModel<HandlersLibraryTreeNode>(props.itemId);

  // Auto-enter rename mode when the kebab "Add Folder" action queued this
  // item's id (Untitled folder just created). Clear the flag right after
  // triggering so a later re-mount of the same id doesn't accidentally
  // re-enter edit mode.
  const dispatch = useDispatch();
  const pendingEditItemId = useSelector(
    (state: RootState) => state.library.pendingEditItemId,
  );
  // Separate flag — set alongside pendingEditItemId when a folder is
  // created via the inline New-Folder flow. Used by the cancel-edit
  // handler to roll the creation back instead of leaving the "Untitled"
  // row persisted (issue #1327). Cleared in DynamicTree.onItemLabelChange
  // on save success.
  const pendingCreateItemId = useSelector(
    (state: RootState) => state.library.pendingCreateItemId,
  );
  const [deleteDirectory] =
    useDeleteApiLibraryDirectoriesByDirectoryIdMutation();
  useEffect(() => {
    if (pendingEditItemId && pendingEditItemId === props.itemId) {
      interactions.toggleItemEditing();
      dispatch(setPendingEditItemId(null));
    }
  }, [pendingEditItemId, props.itemId, interactions, dispatch]);

  // Wrap MUI's cancel handler so that cancelling the rename of an item
  // we just created actually deletes that item. Otherwise Escape leaves
  // a persisted "Untitled" folder (issue #1327) — the folder was created
  // before the rename input opened, so cancel needs to undo it.
  const wrappedHandleCancel = useCallback(
    (event: React.SyntheticEvent) => {
      // Let MUI close the edit input first so the field doesn't try to
      // re-render after we've already deleted the row underneath it.
      interactions.handleCancelItemLabelEditing(event);
      if (
        pendingCreateItemId &&
        pendingCreateItemId === props.itemId &&
        item?.entityType === 'library_directory' &&
        item?.entityId
      ) {
        dispatch(setPendingCreateItemId(null));
        // The rolled-back "Untitled" row disappears on its own: the
        // delete mutation invalidates the 'Library' tag via the
        // enhanceEndpoints override (src/app/athenaApiEnhanced.ts), so the
        // tree refetches without a reload (issue #1333).
        deleteDirectory({ directoryId: item.entityId })
          .unwrap()
          .catch((err) => {
            console.error(
              '[CustomTreeItem] rollback delete failed for pending-create',
              err,
            );
          });
      }
    },
    [
      interactions,
      pendingCreateItemId,
      props.itemId,
      item?.entityType,
      item?.entityId,
      dispatch,
      deleteDirectory,
    ],
  );

  const handleContentDoubleClick: TreeViewCancellableEventHandler<
    React.KeyboardEvent<HTMLInputElement>
  > = (event) => {
    event.defaultMuiPrevented = true;
  };

  const handleInputBlur: TreeViewCancellableEventHandler<
    React.FocusEvent<HTMLInputElement>
  > = (event) => {
    event.defaultMuiPrevented = true;
  };

  return (
    <AthenaTreeItem
      {...props}
      ref={ref}
      slots={{ label: CustomLabel, labelInput: CustomLabelInput }}
      slotProps={{
        label: {
          onDoubleClick: handleContentDoubleClick,
          editable: status.editable,
          editing: status.editing.toString(),
          entityid: props?.itemId,
          entitytype: item?.entityType,
          expanded: status.expanded,
        } as any,
        labelInput: {
          onBlur: handleInputBlur,
          onClick: (e: React.MouseEvent) => e.stopPropagation(),
          handleCancelItemLabelEditing: wrappedHandleCancel,
          handleSaveItemLabel: interactions.handleSaveItemLabel,
        } as any,
      }}
    />
  );
});

export default CustomTreeItem;
